import { useParams } from 'next/navigation';

import { Address } from 'viem';

import {
  useLaunchpadDepositFcfsMutation,
  useLaunchpadDepositProRataMutation,
  useLaunchpadDepositProRataSqrpGatedMutation,
  useLaunchpadDepositSqrpGatedMutation,
  useLaunchpadSaleQuery,
  useUpdateSelfAllocationQuery,
} from '@query';

import {
  LAUNCHPAD_SALE_POOL_TYPE,
  LaunchpadSalePoolType,
} from '@shared/api/types/launchpadProjects';
import { namedWeb3Errors, replaceErrorMessageMap } from '@shared/constants';
import displayWalletErrorToast, {
  WALLET_ERROR_TOAST_ACTION_SCOPE,
} from '@shared/helpers/displayWalletErrorToast';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';

import useDeposit from '@launchpad/hooks/useDeposit';

const useMakeDeposit = () => {
  const params = useParams<{ id: string; saleId: string }>();
  const { data: sale } = useLaunchpadSaleQuery(params.saleId);
  const { mutateAsync: getDepositFCFSInfo } = useLaunchpadDepositFcfsMutation();
  const { mutateAsync: getDepositSQRpGatedInfo } = useLaunchpadDepositSqrpGatedMutation();
  const { mutateAsync: getDepositProRata } = useLaunchpadDepositProRataMutation();
  const { mutateAsync: getDepositProRataSqrpGated } = useLaunchpadDepositProRataSqrpGatedMutation();
  const { deposit } = useDeposit({ type: sale?.attributes.type || 'FCFS' });
  const updateSelfAllocationData = useUpdateSelfAllocationQuery({
    poolType: sale?.attributes.type as LaunchpadSalePoolType,
    poolId: sale?.id as string,
  });

  const getDepositData = async (
    type: LaunchpadSalePoolType,
    { amount, boosted }: { amount: number; boosted: boolean },
  ) => {
    if (type === LAUNCHPAD_SALE_POOL_TYPE.SQRP_GATED) {
      return getDepositSQRpGatedInfo({
        saleId: params.saleId,
        amount,
      });
    }

    if (type === LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA) {
      return getDepositProRata({
        saleId: params.saleId,
        amount,
        boosted,
      });
    }

    if (type === LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED) {
      return getDepositProRataSqrpGated({
        saleId: params.saleId,
        amount,
        boosted,
      });
    }

    return getDepositFCFSInfo({
      saleId: params.saleId,
      amount,
    });
  };

  const makeDeposit = async (
    type: LaunchpadSalePoolType,
    { amount, boosted }: { amount: number; boosted: boolean },
    sqrpLockedAmount: number,
  ) => {
    const depositData = await getDepositData(type, { amount, boosted });

    if (depositData) {
      updateSelfAllocationData(sqrpLockedAmount);
    }

    try {
      const receipt = await deposit({
        amount,
        boosted,
        amountInWei:
          depositData.attributes.signedDepositParams.amountInWei ||
          depositData.attributes.signedDepositParams.baseAmountInWei ||
          '',
        exchangeRateInWei: depositData.attributes.signedDepositParams.boostExchangeRateInWei || '',
        contractAddress: sale?.attributes?.depositContractAddress as Address,
        userId: depositData.user.id,
        walletAddress: depositData.user.attributes.address as Address,
        transactionId: depositData.id,
        timeStampLimit: depositData.attributes.signedDepositParams.timestampLimit,
        signature: depositData.attributes.signedDepositParams.signature as Address,
      });

      const resultTransaction = await handleTransactionResult(receipt);

      return resultTransaction;
    } catch (error: any) {
      if (error?.message) {
        console.error(error.message);
      }

      displayWalletErrorToast({
        error,
        unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
        details: {
          scope: WALLET_ERROR_TOAST_ACTION_SCOPE.UNPAID_GAS_FEES,
          customReplaceMessageSet: [replaceErrorMessageMap.TooLateToSaleGoalReached],
        },
      });
    }
  };

  return { makeDeposit };
};

export default useMakeDeposit;
